import type { Address, PublicClient, WalletClient } from 'viem';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export enum StackName {
  PRIVY = 'privy',
  WAGMI = 'wagmi',
  PRIVY_WAGMI = 'privy_wagmi',
}

export type StackType = StackName.PRIVY | StackName.WAGMI;

export type UIConnector = {
  id: string;
  name: string;
  icon: string;
  connect?: () => Promise<void> | void;
  getProvider?: () => Promise<any> | any;
};

interface Web3ClientsState {
  stack: StackName;
  address: `0x${string}` | null;
  walletClient: WalletClient | null;
  publicClient: PublicClient | null;
  chainId: number | null;
  isAuthenticated: boolean;
  isConnected: boolean;
  isPrivyLoading: boolean;
  isPrivyWallet: boolean;
  ethereumProvider?: any | null;
  connectors?: UIConnector[];
  connectWithConnector?: (id: string) => Promise<void> | void;
  login?: () => Promise<void> | void;
  logout?: () => Promise<void> | void;
  fundPrivyWallet?: (args: { address: Address }) => Promise<void> | void;
  balance?: bigint | null;
}

interface Actions {
  setClients: (patch: Partial<Web3ClientsState>) => void;
  reset: () => void;
  setStack: (stack: StackName) => void;
  setConnectors: (items: UIConnector[] | undefined) => void;
  setConnectWithConnector: (fn: Web3ClientsState['connectWithConnector']) => void;
  setLogin: (fn: Web3ClientsState['login']) => void;
  setLogout: (fn: Web3ClientsState['logout']) => void;
  setEthereumProvider: (p: any | null | undefined) => void;
}

const initialState: Web3ClientsState = {
  address: null,
  walletClient: null,
  publicClient: null,
  chainId: null,
  isAuthenticated: false,
  isConnected: false,
  isPrivyWallet: false,
  ethereumProvider: null,
  stack: null,
  isPrivyLoading: false,
  connectors: undefined,
  connectWithConnector: undefined,
  login: undefined,
  logout: undefined,
  fundPrivyWallet: undefined,
  balance: null,
};

export const blankClients: Partial<Web3ClientsState> = {
  address: null,
  walletClient: null,
  publicClient: null,
  chainId: null,
  isAuthenticated: false,
  isConnected: false,
  isPrivyWallet: false,
  balance: null,
};

export const useWeb3ClientsStore = create<Web3ClientsState & Actions>()(
  persist(
    immer(
      devtools((set) => ({
        ...initialState,

        setClients: (patch) =>
          set((s) => {
            Object.assign(s, patch);
          }),

        reset: () => set(() => ({ ...initialState })),

        setStack: (stack) =>
          set((s) => {
            if (s.stack === stack) return;
            void (s.stack = stack);
            void (s.login = undefined);
          }),
        setConnectors: (items) => set((s) => void (s.connectors = items)),
        setConnectWithConnector: (fn) => set((s) => void (s.connectWithConnector = fn)),
        setLogin: (fn) => set((s) => void (s.login = fn)),
        setLogout: (fn) => set((s) => void (s.logout = fn)),
        setEthereumProvider: (p) => set((s) => void (s.ethereumProvider = p)),
      })),
    ),
    {
      name: 'web3-clients-store',
      // Persist only serializable, safe fields. Exclude clients, providers, functions, and bigint.
      partialize: (state: Web3ClientsState & Actions) => ({
        stack: state.stack,

        // Do not persist: walletClient, publicClient, ethereumProvider, connectors,
        // connectWithConnector, login, logout, fundPrivyWallet, balance (bigint)
      }),
    },
  ),
);
